/**
 * Tenant List Page
 *
 * Displays all tenants with search, filter, and pagination.
 * Capability-gated to users with admin:tenants capability.
 */

import { useQuery } from "@tanstack/react-query";
import {
  Plus,
  Search,
  Loader2,
  AlertCircle,
  RefreshCw,
  Building2,
  ChevronRight,
  Users,
  Folder,
  Wand2,
} from "lucide-react";
import type { ReactElement, ChangeEvent } from "react";
import { useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import { useAuthStore } from "../../../store/authStore";

import type { Tenant, TenantListResponse, TenantListFilters, TenantStatus } from "./types";
import { TENANT_STATUS_INFO } from "./types";

export function TenantListPage(): ReactElement {
  const { hasCapability } = useAuthStore();

  // Check capability
  if (!hasCapability("admin:tenants")) {
    return <Navigate to="/studio" replace />;
  }

  return <TenantListContent />;
}

function TenantListContent(): ReactElement {
  const [filters, setFilters] = useState<TenantListFilters>({
    search: "",
    status: "all",
    page: 1,
    pageSize: 20,
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["tenants", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set("search", filters.search);
      if (filters.status && filters.status !== "all") params.set("status", filters.status);
      if (filters.page) params.set("page", String(filters.page));
      if (filters.pageSize) params.set("pageSize", String(filters.pageSize));

      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await apiRequest<TenantListResponse>(`/api/v1/admin/tenants${query}`);

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
  });

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }));
  }, []);

  const handleStatusChange = useCallback((e: ChangeEvent<HTMLSelectElement>): void => {
    setFilters((prev) => ({
      ...prev,
      status: e.target.value as TenantStatus | "all",
      page: 1,
    }));
  }, []);

  const handlePageChange = useCallback((newPage: number): void => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tenants</h1>
          <p className="mt-1 text-foreground-muted">
            Manage tenant organizations and their settings
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/studio/admin/tenants/onboard" className="btn btn-secondary">
            <Wand2 className="mr-2 h-4 w-4" />
            Onboard New Tenant
          </Link>
          <Link to="/studio/admin/tenants/create" className="btn btn-primary">
            <Plus className="mr-2 h-4 w-4" />
            New Tenant
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
          <input
            type="text"
            placeholder="Search tenants..."
            value={filters.search ?? ""}
            onChange={handleSearchChange}
            className="w-full rounded-md border border-border bg-background py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Status filter */}
        <select
          value={filters.status ?? "all"}
          onChange={handleStatusChange}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load tenants"}
          onRetry={refetch}
        />
      ) : data?.items.length === 0 ? (
        <EmptyState
          hasFilters={Boolean(filters.search || (filters.status && filters.status !== "all"))}
        />
      ) : (
        <>
          {/* Tenant table */}
          <div className="rounded-lg border border-border bg-background-secondary overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Tenant
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Users
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Verticals
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Created
                  </th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data?.items.map((tenant) => (
                  <TenantRow key={tenant.id} tenant={tenant} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && data.total > (filters.pageSize ?? 20) && (
            <Pagination
              page={filters.page ?? 1}
              pageSize={filters.pageSize ?? 20}
              total={data.total}
              hasMore={data.hasMore}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
}

interface TenantRowProps {
  readonly tenant: Tenant;
}

function TenantRow({ tenant }: TenantRowProps): ReactElement {
  const statusInfo = TENANT_STATUS_INFO[tenant.status];

  return (
    <tr className="group hover:bg-background transition-colors">
      <td className="px-4 py-3">
        <Link to={`/studio/admin/tenants/${tenant.externalId}`} className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-foreground group-hover:text-primary transition-colors">
              {tenant.name}
            </p>
            <p className="text-sm text-foreground-muted">{tenant.slug}</p>
          </div>
        </Link>
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            statusInfo.color === "success"
              ? "bg-success/10 text-success"
              : statusInfo.color === "warning"
                ? "bg-warning/10 text-warning"
                : "bg-foreground-muted/10 text-foreground-muted"
          }`}
        >
          {statusInfo.label}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 text-sm text-foreground-muted">
          <Users className="h-4 w-4" />
          {tenant.usersCount ?? 0}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 text-sm text-foreground-muted">
          <Folder className="h-4 w-4" />
          {tenant.verticalsCount ?? 0}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-foreground-muted">
        {new Date(tenant.createdAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/studio/admin/tenants/${tenant.externalId}`}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronRight className="h-5 w-5 text-foreground-muted" />
        </Link>
      </td>
    </tr>
  );
}

// ============================================================================
// State Components
// ============================================================================

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading tenants...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-6 w-6 text-error" />
      </div>
      <p className="mt-4 text-foreground-muted">{message}</p>
      <button onClick={onRetry} className="btn btn-secondary mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </button>
    </div>
  );
}

interface EmptyStateProps {
  readonly hasFilters: boolean;
}

function EmptyState({ hasFilters }: EmptyStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-secondary">
        <Building2 className="h-8 w-8 text-foreground-muted" />
      </div>
      <h3 className="mt-4 font-semibold">
        {hasFilters ? "No tenants match your filters" : "No tenants yet"}
      </h3>
      <p className="mt-2 text-foreground-muted">
        {hasFilters
          ? "Try adjusting your search or filters"
          : "Create your first tenant to get started."}
      </p>
      {!hasFilters && (
        <Link to="/studio/admin/tenants/create" className="btn btn-primary mt-4">
          <Plus className="mr-2 h-4 w-4" />
          Create Tenant
        </Link>
      )}
    </div>
  );
}

// ============================================================================
// Pagination Component
// ============================================================================

interface PaginationProps {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly hasMore: boolean;
  readonly onPageChange: (page: number) => void;
}

function Pagination({
  page,
  pageSize,
  total,
  hasMore,
  onPageChange,
}: PaginationProps): ReactElement {
  const totalPages = Math.ceil(total / pageSize);
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className="mt-4 flex items-center justify-between">
      <p className="text-sm text-foreground-muted">
        Showing {start} to {end} of {total} tenants
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="btn btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-foreground-muted">
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!hasMore}
          className="btn btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
